// Thin client for Better Auth's standard HTTP endpoints.
// Avoids importing better-auth on the frontend (peer-dep conflict with CRA's TypeScript).
// The API shape mirrors better-auth/react's createAuthClient so AuthGate stays unchanged.

import { useState, useEffect, useCallback } from 'react';

const BASE = process.env.REACT_APP_API_URL || '';

async function call(path, body) {
  const res = await fetch(`${BASE}/api/auth${path}`, {
    method: body !== undefined ? 'POST' : 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) return { error: { message: json.message || json.error || 'Request failed' } };
  return { data: json };
}

function useSession() {
  const [data, setData] = useState(undefined);
  const [isPending, setIsPending] = useState(true);

  const refetch = useCallback(async () => {
    const { data: sessionData } = await call('/get-session');
    setData(sessionData ?? null);
    setIsPending(false);
  }, []);

  const signOut = useCallback(async () => {
    await call('/sign-out', {});
    setData(null);
    setIsPending(false);
  }, []);

  useEffect(() => { refetch(); }, [refetch]);

  return { data, isPending, refetch, signOut };
}

export const authClient = {
  useSession,
  signIn: {
    email: ({ email, password }) => call('/sign-in/email', { email, password }),
  },
  signUp: {
    email: ({ email, password, name }) => call('/sign-up/email', { email, password, name }),
  },
  // Send a reset link to the user's email. `redirectTo` is the frontend page the
  // link lands on; Better Auth appends ?token=… to it.
  forgetPassword: ({ email, redirectTo }) => call('/forget-password', { email, redirectTo }),
  // Complete the reset using the token from the emailed link.
  resetPassword: ({ newPassword, token }) => call('/reset-password', { newPassword, token }),
};
